import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Pencil, Rocket, Send, Trash2, UploadCloud } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { Spinner } from '@/components/ui/spinner';
import { useToast } from '@/components/ui/use-toast';
import { PublishTargetDialog } from '@/components/projects/PublishTargetDialog';
import {
  useCreatePublishTarget,
  useDeletePublishTarget,
  useProject,
  usePublishJobs,
  usePublishTargets,
  useRunPublishTarget,
  useUpdatePublishTarget,
  useWorkspaceCredentials,
  useWorkspaceRole,
  type CreatePublishTargetRequest,
  type PublishJob,
  type PublishTarget,
} from '@/lib/api';
import { PUBLISH_TRIGGERS, formatBytes, jobStatusLabel, jobStatusTone } from '@/lib/publish';

const TRIGGER_LABEL = Object.fromEntries(PUBLISH_TRIGGERS.map((t) => [t.value, t.label]));

export function ProjectPublishTab() {
  const { wsId = '', projId = '' } = useParams<{ wsId: string; projId: string }>();
  const project = useProject(projId);
  const { canManage } = useWorkspaceRole(wsId);
  const targets = usePublishTargets(projId);
  const jobs = usePublishJobs(projId);
  // Credentials are owner/admin-only; only fetch when the user can manage to
  // avoid a guaranteed 403 for translators/viewers.
  const credentials = useWorkspaceCredentials(canManage ? wsId : undefined);

  const createTarget = useCreatePublishTarget();
  const updateTarget = useUpdatePublishTarget();
  const deleteTarget = useDeletePublishTarget();
  const runTarget = useRunPublishTarget();
  const toast = useToast();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<PublishTarget | null>(null);
  const [runningId, setRunningId] = useState<string | null>(null);

  const s3Credentials = useMemo(
    () => (credentials.data ?? []).filter((c) => c.kind === 's3'),
    [credentials.data],
  );
  const targetLanguages = project.data?.targetLanguageCodes ?? [];
  const targetName = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of targets.data ?? []) map.set(t.id, t.name);
    return map;
  }, [targets.data]);

  const workspaceSettingsHref = `/workspaces/${wsId}/settings`;

  function openCreate() {
    setEditing(null);
    setDialogOpen(true);
  }

  function openEdit(t: PublishTarget) {
    setEditing(t);
    setDialogOpen(true);
  }

  async function onSubmit(input: CreatePublishTargetRequest) {
    try {
      if (editing) {
        await updateTarget.mutateAsync({ projectID: projId, targetID: editing.id, ...input });
        toast.show(`Updated “${input.name}”`, 'success');
      } else {
        await createTarget.mutateAsync({ projectID: projId, ...input });
        toast.show(`Created “${input.name}”`, 'success');
      }
      setDialogOpen(false);
      setEditing(null);
    } catch (err) {
      toast.show((err as Error).message, 'error');
    }
  }

  async function onRun(t: PublishTarget) {
    setRunningId(t.id);
    try {
      await runTarget.mutateAsync({ projectID: projId, targetID: t.id });
      toast.show(`Publish enqueued for “${t.name}”`, 'success');
    } catch (err) {
      toast.show((err as Error).message, 'error');
    } finally {
      setRunningId(null);
    }
  }

  async function onDelete(t: PublishTarget) {
    if (!window.confirm(`Delete publish target “${t.name}”? This cannot be undone.`)) return;
    try {
      await deleteTarget.mutateAsync({ projectID: projId, targetID: t.id });
      toast.show(`Deleted “${t.name}”`, 'success');
    } catch (err) {
      toast.show((err as Error).message, 'error');
    }
  }

  if (project.isLoading) return <Spinner label="Loading…" />;
  if (!project.data) return null;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex-row items-start justify-between gap-4 space-y-0">
          <div>
            <CardTitle className="inline-flex items-center gap-2">
              <UploadCloud className="size-4 text-primary" aria-hidden />
              Publish targets
            </CardTitle>
            <CardDescription>
              Push exported translations to an S3 bucket — manually, or automatically on import
              and translation changes.
            </CardDescription>
          </div>
          {canManage && (
            <Button size="sm" onClick={openCreate}>
              <Rocket aria-hidden />
              New target
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          {targets.isLoading ? (
            <Spinner label="Loading targets…" />
          ) : targets.isError ? (
            <p
              role="alert"
              className="rounded-md border border-negative/40 bg-negative/10 p-3 text-sm text-negative"
            >
              Failed to load targets: {targets.error.message}
            </p>
          ) : (targets.data ?? []).length === 0 ? (
            <EmptyState
              icon={UploadCloud}
              title="No publish targets yet"
              description={
                canManage
                  ? 'Create an S3 target to start publishing translation files.'
                  : 'An owner or admin can configure a publish destination for this project.'
              }
              action={
                canManage ? (
                  <Button size="sm" onClick={openCreate}>
                    <Rocket aria-hidden />
                    New target
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <ul className="space-y-3">
              {(targets.data ?? []).map((t) => (
                <li
                  key={t.id}
                  className="rounded-lg border border-border bg-card px-4 py-3"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{t.name}</span>
                        <Badge tone={t.enabled ? 'positive' : 'muted'}>
                          {t.enabled ? 'active' : 'paused'}
                        </Badge>
                        <Badge tone="outline">{(t.format ?? 'po').toUpperCase()}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        s3://{t.bucket}
                        {t.prefix ? `/${t.prefix.replace(/^\/+/, '')}` : ''} · {t.region}
                      </p>
                      <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
                        <span className="text-xs text-muted-foreground">
                          {(t.langs ?? []).length === 0
                            ? 'all target languages'
                            : (t.langs ?? []).join(', ')}
                        </span>
                        {(t.triggers ?? []).map((trig) => (
                          <Badge key={trig} tone="primary">
                            {TRIGGER_LABEL[trig] ?? trig}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {canManage && (
                        <>
                          <Button
                            size="sm"
                            onClick={() => void onRun(t)}
                            disabled={runningId === t.id || !t.enabled}
                            title={t.enabled ? undefined : 'Enable the target to publish'}
                          >
                            <Send aria-hidden />
                            {runningId === t.id ? 'Enqueuing…' : 'Publish'}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openEdit(t)}
                            aria-label={`Edit ${t.name}`}
                          >
                            <Pencil aria-hidden />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => void onDelete(t)}
                            disabled={deleteTarget.isPending}
                            aria-label={`Delete ${t.name}`}
                          >
                            <Trash2 aria-hidden />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Run history</CardTitle>
          <CardDescription>
            Recent publish jobs, newest first. Active jobs refresh automatically.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {jobs.isLoading ? (
            <Spinner label="Loading runs…" />
          ) : (jobs.data ?? []).length === 0 ? (
            <EmptyState
              icon={Send}
              title="No runs yet"
              description="Trigger a publish above to see the job here."
            />
          ) : (
            <ul className="divide-y divide-border">
              {(jobs.data ?? []).map((job) => (
                <RunRow key={job.id} job={job} name={targetName.get(job.targetId)} />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <PublishTargetDialog
        open={dialogOpen}
        pending={createTarget.isPending || updateTarget.isPending}
        target={editing}
        credentials={s3Credentials}
        targetLanguages={targetLanguages}
        workspaceSettingsHref={workspaceSettingsHref}
        onClose={() => {
          setDialogOpen(false);
          setEditing(null);
        }}
        onSubmit={onSubmit}
      />
    </div>
  );
}

function RunRow({ job, name }: { job: PublishJob; name: string | undefined }) {
  const when = job.completedAt ?? job.startedAt ?? job.createdAt;
  return (
    <li className="flex flex-wrap items-center justify-between gap-2 py-2.5 text-sm">
      <div className="flex items-center gap-2">
        <Badge tone={jobStatusTone(job.status)}>{jobStatusLabel(job.status)}</Badge>
        <span className="font-medium">{name ?? job.targetId}</span>
        {job.trigger && (
          <span className="text-xs text-muted-foreground">
            via {TRIGGER_LABEL[job.trigger] ?? job.trigger}
          </span>
        )}
      </div>
      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        {job.status === 'succeeded' && job.summary && (
          <span>
            {job.summary.filesWritten ?? 0} file
            {(job.summary.filesWritten ?? 0) === 1 ? '' : 's'} · {formatBytes(job.summary.bytesWritten)}
          </span>
        )}
        {job.status === 'failed' && job.error && (
          <span className="text-negative" title={job.error}>
            {job.error.length > 80 ? `${job.error.slice(0, 80)}…` : job.error}
          </span>
        )}
        {when && <time dateTime={when}>{formatWhen(when)}</time>}
      </div>
    </li>
  );
}

function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
